/**
 * Cookie key for the user's active location preference, namespaced by tenant.
 *
 * Lives in a non-`server-only` module so both server (loader, layout) and
 * client (booking form, native top-bar drawer) can import the same helper.
 * Centralizing the format here means changing it doesn't require chasing
 * down hand-rolled template literals scattered across the app.
 */
export function activeLocationCookieKey(tenantId: string): string {
  return `book:last-location:${tenantId}`;
}

/**
 * In-page event fired whenever a client writes the active-location cookie.
 * The native top-bar chip listens for this so it can re-read its display
 * value without waiting for a navigation or full refresh — important when
 * the user changes location from inside /book (separate React tree from
 * the chip in the app shell).
 *
 * Browser `storage` events would seem like the right tool but they only
 * fire across tabs, not within the same tab where both the writer and the
 * listener live. A custom event keeps it explicit and one-line for any
 * future writer to participate.
 */
export const ACTIVE_LOCATION_CHANGED_EVENT = 'book:active-location-changed';

export interface ActiveLocationChangedDetail {
  tenantId: string;
  slug: string;
}

export function dispatchActiveLocationChanged(
  detail: ActiveLocationChangedDetail,
) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<ActiveLocationChangedDetail>(
      ACTIVE_LOCATION_CHANGED_EVENT,
      { detail },
    ),
  );
}

/** Client-only: the saved venue slug for this tenant, or null when unset. */
export function readActiveLocationSlug(tenantId: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${activeLocationCookieKey(tenantId)}=`;
  const row = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix));
  if (!row) return null;
  // Read during render (useSyncExternalStore), so a malformed value written
  // by anything else must degrade to "no venue" rather than throw.
  try {
    return decodeURIComponent(row.slice(prefix.length)) || null;
  } catch {
    return null;
  }
}

/**
 * Client-only: saves `slug` as the tenant's active venue and notifies in-tab
 * listeners. The cookie is what the SSR resolver (getActiveLocation) reads;
 * localStorage is a fallback for /book's rehydration when cookies are
 * partitioned. Callers that need server data re-scoped follow this with
 * `router.refresh()`.
 */
export function persistActiveLocation(tenantId: string, slug: string) {
  if (typeof window === 'undefined') return;
  const key = activeLocationCookieKey(tenantId);
  // Secure only on https so the cookie still sets on http://localhost.
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${key}=${encodeURIComponent(slug)};path=/;max-age=31536000;SameSite=Lax${secure}`;
  try {
    window.localStorage.setItem(key, slug);
  } catch {
    // Storage disabled — the cookie is the source of truth anyway.
  }
  dispatchActiveLocationChanged({ tenantId, slug });
}

/**
 * Shape of a `court_locations (slug, is_active, is_deleted)` embed. Pages
 * that already join the venue for other reasons add these three columns and
 * pass the row through here instead of running a second lookup.
 */
export interface EmbeddedLocationRow {
  slug: string | null;
  is_active: boolean | null;
  is_deleted: boolean | null;
}

/** Slug of an embedded venue, or null when it is archived / soft-deleted —
 *  a stale slug in the cookie would otherwise fall through to "no venue". */
export function liveLocationSlug(
  row: EmbeddedLocationRow | null | undefined,
): string | null {
  if (!row?.slug || row.is_active === false || row.is_deleted === true) {
    return null;
  }
  return row.slug;
}
